import { useEffect, useState } from "react";
import { Plus, Trash2, Copy, CheckCircle2, RefreshCw, Send, KeyRound } from "lucide-react";
import api, { formatApiError } from "@/lib/api";

const KINDS = [
  { value: "memoire", label: "Mémoire" },
  { value: "projet_tutore", label: "Projet tutoré" },
  { value: "any", label: "Mémoire ou projet tutoré" },
];

function useLists() {
  const [lists, setLists] = useState({ faculties: [], departments: [], promotions: [] });
  useEffect(() => {
    Promise.all([
      api.get("/lists/faculties"),
      api.get("/lists/departments"),
      api.get("/lists/promotions"),
    ]).then(([f, d, p]) =>
      setLists({ faculties: f.data, departments: d.data, promotions: p.data })
    );
  }, []);
  return lists;
}

function CopyBtn({ text }) {
  const [copied, setCopied] = useState(false);
  return (
    <button
      onClick={(e) => {
        e.stopPropagation();
        navigator.clipboard.writeText(text);
        setCopied(true);
        setTimeout(() => setCopied(false), 1500);
      }}
      className="text-slate-500 hover:text-unisic-blue"
      title="Copier"
      data-testid={`copy-${text}`}
    >
      {copied ? <CheckCircle2 className="w-3.5 h-3.5 text-green-600" /> : <Copy className="w-3.5 h-3.5" />}
    </button>
  );
}

export default function StudentCodes() {
  const { faculties, departments, promotions } = useLists();
  const [items, setItems] = useState([]);
  const [showForm, setShowForm] = useState(false);
  const [form, setForm] = useState({
    student_name: "",
    student_email: "",
    phone: "",
    faculty: "",
    department: "",
    promotion: "",
    kind: "memoire",
    note: "",
  });
  const [filter, setFilter] = useState("all");
  const [error, setError] = useState("");
  const [lastCreated, setLastCreated] = useState(null);

  const load = async () => {
    const params = {};
    if (filter === "used") params.used = true;
    if (filter === "unused") params.used = false;
    const { data } = await api.get("/admin/student-codes", { params });
    setItems(data);
  };

  useEffect(() => { load(); /* eslint-disable-next-line */ }, [filter]);

  const submit = async (e) => {
    e.preventDefault();
    setError("");
    setLastCreated(null);
    try {
      const { data } = await api.post("/admin/student-codes", form);
      setLastCreated(data);
      setForm({ student_name: "", student_email: "", phone: "", faculty: "", department: "", promotion: "", kind: "memoire", note: "" });
      setShowForm(false);
      load();
    } catch (err) { setError(formatApiError(err)); }
  };

  const del = async (id) => {
    if (!window.confirm("Supprimer ce code ? Cette action est définitive.")) return;
    await api.delete(`/admin/student-codes/${id}`);
    load();
  };

  return (
    <div data-testid="student-codes-page">
      <div className="flex items-start justify-between flex-wrap gap-4">
        <div>
          <div className="eyebrow eyebrow-dark">Service des Dépôts</div>
          <h1 className="mt-2 text-3xl font-bold text-unisic-blue">Codes d'accès étudiants</h1>
          <p className="mt-2 text-slate-600 text-sm max-w-2xl">
            Générez et transmettez un code d'accès à chaque étudiant autorisé à déposer un mémoire
            ou un projet tutoré. Le code est envoyé automatiquement par e-mail et pré-remplit le
            formulaire de dépôt.
          </p>
        </div>
        <button
          onClick={() => setShowForm(!showForm)}
          className="btn-primary text-sm"
          data-testid="btn-new-code"
        >
          <Plus className="w-4 h-4" /> {showForm ? "Fermer" : "Nouveau code"}
        </button>
      </div>
      <span className="divider-gold mt-3" />

      {lastCreated && (
        <div className="mt-6 paper-card p-6 bg-unisic-blue text-white flex items-center justify-between gap-4 flex-wrap" data-testid="last-code-banner">
          <div>
            <div className="eyebrow" style={{ color: "#E5C158" }}>Code généré</div>
            <div className="mt-2 flex items-center gap-3">
              <KeyRound className="w-5 h-5 text-unisic-gold" />
              <div className="font-mono text-2xl tracking-widest">{lastCreated.code}</div>
              <button
                onClick={() => navigator.clipboard.writeText(lastCreated.code)}
                className="text-xs text-unisic-gold hover:underline"
              >
                Copier
              </button>
            </div>
            <div className="mt-2 text-sm text-white/80">
              Envoyé à {lastCreated.student_email} — {lastCreated.student_name}
            </div>
          </div>
          <Send className="w-6 h-6 text-unisic-gold opacity-70" />
        </div>
      )}

      {showForm && (
        <form onSubmit={submit} className="mt-6 paper-card p-6 grid gap-4 md:grid-cols-2" data-testid="code-form">
          <div>
            <label className="text-xs font-semibold uppercase tracking-widest text-slate-600">
              Nom complet de l'étudiant <span className="text-unisic-red">*</span>
            </label>
            <input required value={form.student_name} onChange={(e) => setForm({ ...form, student_name: e.target.value })} className="mt-1 w-full border border-slate-300 px-3 py-2.5 rounded-sm text-sm" data-testid="input-student-name" />
          </div>
          <div>
            <label className="text-xs font-semibold uppercase tracking-widest text-slate-600">
              E-mail <span className="text-unisic-red">*</span>
            </label>
            <input required type="email" value={form.student_email} onChange={(e) => setForm({ ...form, student_email: e.target.value })} className="mt-1 w-full border border-slate-300 px-3 py-2.5 rounded-sm text-sm" data-testid="input-student-email" />
          </div>
          <div>
            <label className="text-xs font-semibold uppercase tracking-widest text-slate-600">Téléphone</label>
            <input value={form.phone} onChange={(e) => setForm({ ...form, phone: e.target.value })} className="mt-1 w-full border border-slate-300 px-3 py-2.5 rounded-sm text-sm" />
          </div>
          <div>
            <label className="text-xs font-semibold uppercase tracking-widest text-slate-600">
              Autorisation <span className="text-unisic-red">*</span>
            </label>
            <select value={form.kind} onChange={(e) => setForm({ ...form, kind: e.target.value })} className="mt-1 w-full border border-slate-300 px-3 py-2.5 rounded-sm text-sm bg-white" data-testid="input-kind">
              {KINDS.map((k) => <option key={k.value} value={k.value}>{k.label}</option>)}
            </select>
          </div>
          <div>
            <label className="text-xs font-semibold uppercase tracking-widest text-slate-600">
              Faculté <span className="text-unisic-red">*</span>
            </label>
            <select required value={form.faculty} onChange={(e) => setForm({ ...form, faculty: e.target.value })} className="mt-1 w-full border border-slate-300 px-3 py-2.5 rounded-sm text-sm bg-white">
              <option value="">Sélectionner...</option>
              {faculties.map((f) => <option key={f.id} value={f.name}>{f.name}</option>)}
            </select>
          </div>
          <div>
            <label className="text-xs font-semibold uppercase tracking-widest text-slate-600">
              Département <span className="text-unisic-red">*</span>
            </label>
            <select required value={form.department} onChange={(e) => setForm({ ...form, department: e.target.value })} className="mt-1 w-full border border-slate-300 px-3 py-2.5 rounded-sm text-sm bg-white">
              <option value="">Sélectionner...</option>
              {departments.map((d) => <option key={d.id} value={d.name}>{d.name}</option>)}
            </select>
          </div>
          <div>
            <label className="text-xs font-semibold uppercase tracking-widest text-slate-600">
              Promotion <span className="text-unisic-red">*</span>
            </label>
            <select required value={form.promotion} onChange={(e) => setForm({ ...form, promotion: e.target.value })} className="mt-1 w-full border border-slate-300 px-3 py-2.5 rounded-sm text-sm bg-white">
              <option value="">Sélectionner...</option>
              {promotions.map((p) => <option key={p.id} value={p.name}>{p.name}</option>)}
            </select>
          </div>
          <div>
            <label className="text-xs font-semibold uppercase tracking-widest text-slate-600">Note interne (facultatif)</label>
            <input value={form.note} onChange={(e) => setForm({ ...form, note: e.target.value })} className="mt-1 w-full border border-slate-300 px-3 py-2.5 rounded-sm text-sm" />
          </div>
          {error && <div className="md:col-span-2 text-sm text-unisic-red bg-red-50 border border-red-100 px-3 py-2 rounded-sm">{error}</div>}
          <div className="md:col-span-2 flex justify-end">
            <button type="submit" className="btn-primary text-sm" data-testid="btn-generate">
              <KeyRound className="w-4 h-4" /> Générer le code
            </button>
          </div>
        </form>
      )}

      <div className="mt-8 flex items-center gap-2 flex-wrap">
        <span className="text-xs uppercase tracking-widest text-slate-500">Filtrer :</span>
        {[
          { v: "all", l: "Tous" },
          { v: "unused", l: "Non utilisés" },
          { v: "used", l: "Utilisés" },
        ].map((f) => (
          <button
            key={f.v}
            onClick={() => setFilter(f.v)}
            className={`text-xs px-3 py-1.5 rounded-sm border transition-colors ${
              filter === f.v
                ? "bg-unisic-blue text-white border-unisic-blue"
                : "bg-white text-slate-700 border-slate-300 hover:border-unisic-gold"
            }`}
            data-testid={`filter-${f.v}`}
          >
            {f.l}
          </button>
        ))}
        <button
          onClick={load}
          className="ml-2 text-xs text-slate-500 hover:text-unisic-blue flex items-center gap-1"
        >
          <RefreshCw className="w-3 h-3" /> Rafraîchir
        </button>
      </div>

      <div className="mt-4 paper-card overflow-hidden">
        <table className="unisic-table w-full">
          <thead className="bg-slate-50 border-b border-slate-200">
            <tr>
              <th className="px-4 py-3">Code</th>
              <th className="px-4 py-3">Étudiant</th>
              <th className="px-4 py-3">Département / Promotion</th>
              <th className="px-4 py-3">Type</th>
              <th className="px-4 py-3">Statut</th>
              <th className="px-4 py-3"></th>
            </tr>
          </thead>
          <tbody>
            {items.length === 0 && (
              <tr><td colSpan={6} className="text-center py-10 text-slate-500">Aucun code généré.</td></tr>
            )}
            {items.map((c) => (
              <tr key={c.id} className="hover:bg-slate-50" data-testid={`code-row-${c.id}`}>
                <td className="px-4 py-3">
                  <div className="flex items-center gap-2">
                    <span className="font-mono text-sm font-semibold text-unisic-blue">{c.code}</span>
                    <CopyBtn text={c.code} />
                  </div>
                </td>
                <td className="px-4 py-3">
                  <div className="font-medium">{c.student_name}</div>
                  <div className="text-xs text-slate-500">{c.student_email}</div>
                </td>
                <td className="px-4 py-3 text-xs">
                  {c.department}<br />
                  <span className="text-slate-500">{c.promotion} · {c.faculty}</span>
                </td>
                <td className="px-4 py-3 text-xs uppercase">{c.kind === "memoire" ? "Mémoire" : c.kind === "projet_tutore" ? "Projet" : "Mémoire/Projet"}</td>
                <td className="px-4 py-3">
                  {c.used ? (
                    <span className="status-pill bg-green-50 text-green-700 border-green-100">
                      <CheckCircle2 className="w-3 h-3" /> Utilisé
                    </span>
                  ) : (
                    <span className="status-pill bg-amber-50 text-amber-800 border-amber-100">
                      Disponible
                    </span>
                  )}
                </td>
                <td className="px-4 py-3 text-right">
                  {!c.used && (
                    <button onClick={() => del(c.id)} className="text-slate-400 hover:text-unisic-red" data-testid={`btn-del-${c.id}`}>
                      <Trash2 className="w-3.5 h-3.5" />
                    </button>
                  )}
                </td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>
    </div>
  );
}
